export type Role = "platform_owner" | "workshop_owner" | "employee" | "client";

export type UserOut = {
  id: number;
  full_name: string;
  phone: string;
  role: Role;
  workshop_id: number | null;
  branch_id: number | null;
  can_be_usta?: boolean;
  can_be_sotuvchi?: boolean;
};

export type TokenOut = { access_token: string; user: UserOut };

export type ClientNextDueItem = {
  car_id: number;
  plate_number: string;
  service_name: string;
  due_at: string;
  due_km: number | null;
  current_km: number;
  workshop_name: string;
};

// Birlashtirilgan garaj + qo'lda xarajatlar (Mini App v2)
export type ExpenseCategory =
  | "fuel"
  | "insurance"
  | "repair_external"
  | "parts"
  | "carwash"
  | "other"
  | "oil"
  | "tire"
  | "fine"
  | "inspection";

export type Period = "month" | "year" | "all";

export type ClientReminderType = "sugurta" | "texnik_korik" | "moy" | "boshqa";

export type MergedCarOut = {
  normalized_plate: string;
  display_plate: string;
  model: string | null;
  year: number | null;
  total_spent: number;
  next_service_hint: ClientNextDueItem | null;
  is_self_added: boolean;
  client_car_id: number | null;
};

// Self qo'shilgan avto (client-scoped)
export type ManualCarOut = {
  id: number;
  normalized_plate: string;
  display_plate: string;
  model: string | null;
  year: number | null;
};

export type ManualCarPayload = {
  plate: string;
  model: string | null;
  year: number | null;
};

export type ExpenseOut = {
  id: number;
  normalized_plate: string;
  category: ExpenseCategory;
  custom_label: string | null;
  amount: number;
  spent_at: string;
  note: string | null;
  odometer_km: number | null;
  litr: number | null;
  fuel_type: string | null;
};

export type TimelineWorkItem = {
  kind: "work";
  id: number;
  date: string;
  service_name: string;
  price: number;
  workshop_name: string;
};

export type TimelineExpenseItem = {
  kind: "expense";
  date: string;
  expense: ExpenseOut;
};

export type TimelineItem = TimelineWorkItem | TimelineExpenseItem;

export type CategoryBreakdownItem = { category: string; total: number };

export type MergedCarDetailOut = {
  normalized_plate: string;
  display_plate: string;
  model: string | null;
  year: number | null;
  total_spent: number;
  month_total: number;
  period: Period;
  period_total: number;
  breakdown: CategoryBreakdownItem[];
  timeline: TimelineItem[];
  next_service_hint: ClientNextDueItem | null;
  is_self_added: boolean;
  client_car_id: number | null;
};

export type ExpensePayload = {
  category: ExpenseCategory;
  custom_label: string | null;
  amount: number;
  spent_at: string;
  note: string | null;
  odometer_km: number | null;
  litr: number | null;
  fuel_type: string | null;
};

export type FuelEconomyByType = {
  fuel_type: string | null;
  l_per_100km: number | null;
  cost_per_km: number | null;
  sample_count: number;
};

export type FuelEconomyOut = {
  has_data: boolean;
  latest_l_per_100km: number | null;
  avg_l_per_100km: number | null;
  latest_cost_per_km: number | null;
  avg_cost_per_km: number | null;
  by_fuel_type: FuelEconomyByType[];
};

export type WorkshopContactOut = {
  workshop_id: number;
  name: string;
  phone: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  location_url: string | null;
  description: string | null;
  work_hours: string | null;
  instagram: string | null;
  telegram: string | null;
  website: string | null;
};

export type WorkshopRatingEligibility = {
  eligible: boolean;
  work_id: number | null;
  visit_date: string | null;
};

export type WorkshopRatingOut = {
  id: number;
  stars: number;
  comment: string | null;
  submitted_at: string;
};

export type ReminderStatusKind = "overdue" | "due_soon" | "upcoming" | "scheduled";

export type ReminderListItem = {
  source: "workshop" | "client";
  id: number;
  plate: string;
  title: string;
  type: ClientReminderType | null;
  due_at: string | null;
  due_km: number | null;
  current_km: number | null;
  status: ReminderStatusKind;
  note: string | null;
  workshop_name: string | null;
  editable: boolean;
};

export type ClientReminderOut = {
  id: number;
  normalized_plate: string;
  type: ClientReminderType;
  title: string | null;
  due_at: string | null;
  due_km: number | null;
  note: string | null;
  status: string;
};

export type ReminderPayload = {
  plate: string;
  type: ClientReminderType;
  title: string | null;
  due_at: string | null;
  due_km: number | null;
  note: string | null;
};

export type ApiError = { code: string; message: string; status: number };

const BASE = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/+$/, "") + "/api/v1";
let token: string | null = null;

export function setToken(t: string | null) {
  token = t;
  if (t) localStorage.setItem("an_token", t);
  else localStorage.removeItem("an_token");
}

export function loadToken(): string | null {
  if (token) return token;
  token = localStorage.getItem("an_token");
  return token;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const r = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!r.ok) {
    let body: { error?: { code?: string; message?: string } } = {};
    try {
      body = await r.json();
    } catch {
      // ignore
    }
    const err: ApiError = {
      code: body.error?.code || `http_${r.status}`,
      message: body.error?.message || r.statusText,
      status: r.status,
    };
    throw err;
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

export const api = {
  authTelegram: (initData: string) =>
    req<TokenOut>("/auth/telegram", {
      method: "POST",
      body: JSON.stringify({ init_data: initData }),
    }),
  me: () => req<UserOut>("/auth/me"),
  // Birlashtirilgan garaj + qo'lda xarajatlar
  clientGarage: () => req<MergedCarOut[]>(`/client/garage`),
  clientGarageDetail: (plate: string, period: Period = "all") =>
    req<MergedCarDetailOut>(
      `/client/garage/${encodeURIComponent(plate)}?period=${period}`,
    ),
  clientFuelEconomy: (plate: string) =>
    req<FuelEconomyOut>(
      `/client/garage/${encodeURIComponent(plate)}/fuel-economy`,
    ),
  clientCarWorkshops: (plate: string) =>
    req<WorkshopContactOut[]>(
      `/client/garage/${encodeURIComponent(plate)}/workshops`,
    ),
  clientWorkshops: () => req<WorkshopContactOut[]>(`/client/workshops`),
  clientWorkshopRatingEligibility: (workshopId: number) =>
    req<WorkshopRatingEligibility>(
      `/client/workshops/${workshopId}/rating-eligibility`,
    ),
  clientRateWorkshop: (
    workshopId: number,
    payload: { stars: number; comment: string | null },
  ) =>
    req<WorkshopRatingOut>(`/client/workshops/${workshopId}/ratings`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  clientCreateExpense: (payload: ExpensePayload & { plate: string }) =>
    req<ExpenseOut>(`/client/expenses`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  clientUpdateExpense: (id: number, payload: ExpensePayload) =>
    req<ExpenseOut>(`/client/expenses/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  clientDeleteExpense: (id: number) =>
    req<void>(`/client/expenses/${id}`, { method: "DELETE" }),
  // Self qo'shilgan avtolar (client-scoped)
  clientCreateCar: (payload: ManualCarPayload) =>
    req<ManualCarOut>(`/client/cars`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  clientUpdateCar: (id: number, payload: ManualCarPayload) =>
    req<ManualCarOut>(`/client/cars/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  clientDeleteCar: (id: number) =>
    req<void>(`/client/cars/${id}`, { method: "DELETE" }),
  // Mijoz eslatmalari (birlashtirilgan ro'yxat + CRUD)
  clientReminders: () => req<ReminderListItem[]>(`/client/reminders`),
  clientCreateReminder: (payload: ReminderPayload) =>
    req<ClientReminderOut>(`/client/reminders`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  clientUpdateReminder: (id: number, payload: ReminderPayload) =>
    req<ClientReminderOut>(`/client/reminders/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  clientDeleteReminder: (id: number) =>
    req<void>(`/client/reminders/${id}`, { method: "DELETE" }),
  // Ustaxona xabarlaridan bosh tortish (opt-out) — workshop_id ro'yxati.
  clientOptOuts: () => req<number[]>(`/client/opt-outs`),
  clientOptOut: (workshopId: number) =>
    req<void>(`/client/opt-out`, {
      method: "POST",
      body: JSON.stringify({ workshop_id: workshopId }),
    }),
  clientOptIn: (workshopId: number) =>
    req<void>(`/client/opt-out/${workshopId}`, { method: "DELETE" }),
};
